import { IconBook2, IconSignature, IconUpload } from "@tabler/icons-react"
import { type FC, type FormEvent, useState } from "react"
import { useDropzone } from "react-dropzone"
import { Link, useNavigate } from "react-router"

import { getDataUrlByFile, supportedFileExtensions } from "@forento/core/utilities/file"
import { cn } from "@forento/ui/utilities/tailwind"

import { Input } from "~/components/input"
import Layout from "~/components/layout"
import RichContentEditor from "~/components/rich-content-editor"
import { exportRichContent, type RichContentValue } from "~/utilities/rich-content"
import { query } from "~/utilities/trpc"

const CreateCoursePage: FC = () => {
	const navigate = useNavigate()

	const [name, setName] = useState("")
	const [shortDescription, setShortDescription] = useState("")
	const [presentation, setPresentation] = useState<RichContentValue | null>(null)
	const [thumbnailFile, setThumbnailFile] = useState<File | null>(null)
	const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null)
	const [error, setError] = useState<string>()

	const create = query.createCourse.useMutation({
		onMutate: () => setError(undefined),
		onSuccess: () => navigate("/courses"),
		onError(error) {
			console.error(error)
			setError("Ett fel uppstod när kursen skulle skapas. Försök igen.")
		},
	})

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop(acceptedFiles) {
			const file = acceptedFiles[0]
			if (!file) return

			setThumbnailFile(file)
			const reader = new FileReader()
			reader.onload = e => setThumbnailPreview(e.target?.result as string)
			reader.readAsDataURL(file)
		},
		accept: Object.fromEntries(supportedFileExtensions.image.map(x => [`image/${x.toLowerCase()}`, []])),
	})

	async function handleSubmit(event: FormEvent) {
		event.preventDefault()

		if (!name.trim() || !shortDescription.trim()) {
			setError("Titel och beskrivning är obligatoriska")
			return
		}

		create.mutate({
			name: name.trim(),
			shortDescription: shortDescription.trim(),
			presentation: exportRichContent(presentation),
			thumbnailBase64: thumbnailFile ? await getDataUrlByFile(thumbnailFile) : null,
		})
	}

	return (
		<Layout>
			<div className="mx-auto flex w-full max-w-4xl flex-col gap-8 py-10">
				<div className="flex items-center gap-4">
					<IconBook2 className="text-primary size-8" />
					<div>
						<h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Skapa ny kurs</h1>
						<p className="mt-1 text-sm text-slate-600">
							Fyll i informationen nedan för att skapa en ny kurs
						</p>
					</div>
				</div>

				{error && (
					<div className="rounded-lg border border-red-200 bg-red-50 p-4">
						<p className="text-sm text-red-700">{error}</p>
					</div>
				)}

				<form onSubmit={handleSubmit} className="space-y-8">
					<div className="rounded-lg border border-slate-200 bg-white p-6">
						<h2 className="mb-6 text-lg font-semibold text-slate-900">Grundläggande information</h2>
						<div className="space-y-6">
							<Input
								type="text"
								label="Kursnamn"
								value={name}
								onChange={setName}
								placeholder="Ange kursnamn"
								icon={IconSignature}
								required
							/>

							<div>
								<label className="mb-2 block text-sm font-medium text-slate-700">
									Kort beskrivning
								</label>
								<textarea
									value={shortDescription}
									onChange={e => setShortDescription(e.target.value)}
									placeholder="Beskriv kursen i korthet"
									className="focus:border-primary focus:ring-primary w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-500 focus:ring-1 focus:outline-none"
									rows={3}
								/>
							</div>

							<div>
								<label className="mb-2 block text-sm font-medium text-slate-700">
									Presentation (valfritt)
								</label>
								<RichContentEditor
									placeholder="Beskriv kursen i längre detalj"
									initialValue={null}
									setValue={setPresentation}
								/>
							</div>
						</div>
					</div>

					<div className="rounded-lg border border-slate-200 bg-white p-6">
						<h2 className="mb-6 text-lg font-semibold text-slate-900">Kursbild</h2>
						<div className="space-y-4">
							<div className="flex items-center gap-4">
								<div
									{...getRootProps()}
									className={cn(
										"flex aspect-video w-48 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed transition-colors duration-200",
										isDragActive
											? "border-primary bg-primary/5"
											: "border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100",
									)}
								>
									<input {...getInputProps()} />
									{thumbnailPreview ? (
										<img
											src={thumbnailPreview}
											alt="Thumbnail preview"
											className="h-full w-full rounded-lg object-cover"
										/>
									) : (
										<div className="flex flex-col items-center text-slate-400">
											<IconUpload className="mb-2 size-8" />
											<span className="text-sm">
												{isDragActive ? "Släpp bilden här" : "Klicka eller släpp bild"}
											</span>
										</div>
									)}
								</div>
								<div className="flex-1">
									<label className="mb-2 block text-sm font-medium text-slate-700">
										Ladda upp kursbild
									</label>
									<p className="text-xs text-slate-500">Rekommenderad storlek: 1280x720 pixlar.</p>
									<p className="mt-1 text-xs text-slate-500">
										Klicka på bildrutan eller dra och släpp en bild för att ladda upp.
									</p>
								</div>
							</div>
						</div>
					</div>

					<div className="flex justify-end gap-4">
						<Link
							to="/courses"
							className="focus:ring-primary rounded-lg border border-slate-300 bg-white px-6 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:ring-2 focus:ring-offset-2 focus:outline-none"
						>
							Avbryt
						</Link>
						<button
							type="submit"
							disabled={create.isPending || !name.trim() || !shortDescription.trim()}
							className={cn(
								"rounded-lg px-6 py-3 text-sm font-semibold text-white transition-colors duration-200",
								create.isPending || !name.trim() || !shortDescription.trim()
									? "cursor-not-allowed bg-slate-400"
									: "bg-primary hover:bg-primary/90 focus:ring-primary cursor-pointer focus:ring-2 focus:ring-offset-2 focus:outline-none",
							)}
						>
							{create.isPending ? "Skapar kurs..." : "Skapa kurs"}
						</button>
					</div>
				</form>
			</div>
		</Layout>
	)
}

export default CreateCoursePage
